import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown } from 'lucide-react';
import { COMPREHENSIVE_TIMEZONES, searchTimezones, getTimezoneOption, type TimezoneOption } from '../utils/timezoneList';

interface SearchableTimezonePickerProps {
  value?: string;
  onChange: (timezone: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
}

export const SearchableTimezonePicker: React.FC<SearchableTimezonePickerProps> = ({
  value = '',
  onChange,
  onBlur,
  placeholder = 'Select timezone',
  required = false,
  disabled = false,
  className = '',
  id
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filteredTimezones, setFilteredTimezones] = useState<TimezoneOption[]>(COMPREHENSIVE_TIMEZONES);
  const [selectedTimezone, setSelectedTimezone] = useState<TimezoneOption | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update filtered timezones when search query changes
  useEffect(() => {
    const filtered = searchTimezones(searchQuery, COMPREHENSIVE_TIMEZONES);
    setFilteredTimezones(filtered);
  }, [searchQuery]);

  // Initialize selected timezone when value changes
  useEffect(() => {
    if (value) {
      const option = getTimezoneOption(value, COMPREHENSIVE_TIMEZONES);
      setSelectedTimezone(option);
    } else {
      setSelectedTimezone(null);
    }
  }, [value]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSearchQuery('');
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleTimezoneSelect = (timezone: TimezoneOption) => {
    setSelectedTimezone(timezone);
    onChange(timezone.value);
    setIsDropdownOpen(false);
    setSearchQuery('');
    
    // Call onBlur if provided (for form validation)
    if (onBlur) {
      onBlur();
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
    }
  };

  const handleDropdownToggle = () => {
    if (disabled) return;
    setIsDropdownOpen(!isDropdownOpen);
  };

  return (
    <div 
      className={`timezone-picker ${className} ${disabled ? 'disabled' : ''} ${isDropdownOpen ? 'open' : ''}`}
      onClick={handleDropdownToggle}
      id={id}
      ref={dropdownRef}
    >
      <div className="selected-timezone">
        {selectedTimezone ? selectedTimezone.label : placeholder}
      </div>
      <ChevronDown className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`} size={20} />

      {isDropdownOpen && !disabled && (
        <div className="timezone-dropdown">
          <div className="search-input-container">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search timezones (e.g., 'pacific', 'london', 'tokyo')"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="timezone-search-input"
              autoFocus
            />
          </div>

          <div className="timezone-options">
            {filteredTimezones.length > 0 ? (
              filteredTimezones.map((option) => (
                <div
                  key={option.value}
                  className={`timezone-option ${selectedTimezone?.value === option.value ? 'selected' : ''}`}
                  onClick={() => handleTimezoneSelect(option)}
                >
                  <div className="timezone-option-main">
                    <div className="timezone-label">{option.label}</div>
                    <div className="timezone-offset">{option.utcOffset}</div>
                  </div>
                  <div className="timezone-region">{option.region}</div>
                </div>
              ))
            ) : (
              <div className="no-results">
                <Search size={16} />
                <div>No timezones found matching "{searchQuery}"</div>
                <div className="search-suggestions">Try searching for a city, region, or abbreviation</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hidden input for form compatibility */}
      <input
        type="hidden"
        name={id}
        value={value}
        required={required}
      />

      <style>{`
        .timezone-picker {
          width: 100%;
          min-width: 280px;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          white-space: nowrap;
          border: 2px solid var(--bg-tertiary);
          border-radius: var(--radius-medium);
          background: var(--bg-primary);
          cursor: pointer;
          transition: all 0.2s ease;
          min-height: 48px;
        }

        .timezone-picker:hover:not(.disabled) {
          border-color: var(--accent-blue);
        }

        .timezone-picker.open {
          border-color: var(--accent-blue);
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }

        .timezone-picker.disabled {
          opacity: 0.6;
          cursor: not-allowed;
          background: var(--bg-secondary);
        }

        .selected-timezone {
          flex: 1;
          font-size: 14px;
          font-weight: 500;
          color: var(--text-primary);
          text-align: left;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .dropdown-arrow {
          transition: transform 0.2s ease;
          color: var(--text-secondary);
          flex-shrink: 0;
        }

        .dropdown-arrow.open {
          transform: rotate(180deg);
        }

        .timezone-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          z-index: 1000;
          background: var(--bg-primary);
          border: 2px solid var(--accent-blue);
          border-top: none;
          border-radius: 0 0 var(--radius-medium) var(--radius-medium);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          max-height: 400px;
          min-width: 420px;
          width: max-content;
          overflow: hidden;
        }

        .search-input-container {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          border-bottom: 1px solid var(--bg-tertiary);
          background: var(--bg-secondary);
        }

        .search-input-container svg {
          color: var(--text-secondary);
          flex-shrink: 0;
        }

        .timezone-search-input {
          flex: 1;
          border: none;
          background: transparent;
          color: var(--text-primary);
          font-size: 14px;
          outline: none;
        }

        .timezone-search-input::placeholder {
          color: var(--text-tertiary);
        }

        .timezone-options {
          max-height: 320px;
          overflow-y: auto;
        }

        .timezone-option {
          padding: 12px 16px;
          cursor: pointer;
          transition: background-color 0.15s ease;
          border-bottom: 1px solid var(--bg-tertiary);
          white-space: nowrap;
        }

        .timezone-option:last-child {
          border-bottom: none;
        }

        .timezone-option:hover {
          background: var(--bg-secondary);
        }

        .timezone-option.selected {
          background: rgba(var(--accent-blue-rgb), 0.1);
        }

        .timezone-option-main {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 2px;
        }

        .timezone-label {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-primary);
        }

        .timezone-offset {
          font-size: 12px;
          color: var(--text-secondary);
          font-family: monospace;
        }

        .timezone-region {
          font-size: 12px;
          color: var(--text-tertiary);
          font-style: italic;
        }

        .no-results {
          padding: 24px;
          text-align: center;
          color: var(--text-secondary);
        }

        .no-results svg {
          margin-bottom: 8px;
          opacity: 0.5;
        }

        .search-suggestions {
          font-size: 12px;
          color: var(--text-tertiary);
          margin-top: 4px;
        }

        /* Responsive design */
        @media (max-width: 768px) {
          .timezone-dropdown {
            max-height: 300px;
          }

          .timezone-options {
            max-height: 220px;
          }

          .timezone-option {
            padding: 10px 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default SearchableTimezonePicker;